import { createSlice } from "@reduxjs/toolkit";
import {
  fetchCompanyQuotaData,
  fetchQuotaData,
  fetchQuotaFiscalQuartersFilters,
  fetchTeamQuotaData,
  getTeamAndFiscalQuarters,
  updateCompanyQuotaData,
  updateQuotaData,
  updateTeamQuotaData,
  removeQuotaForReps,
  deleteQuotaRemoval,
  getQuotaAddingReps,
  getQuotaAddingRepInfo,
  removeQuotaForRepsQuarterInfo,
  getQuotaAddingQuarters,
  createQuotaInfo,
  createQuotaInfoOverride,
  bulkUpsertQuotaData,
  bulkUpsertTeamQuotaData,
  bulkUpsertCompanyQuotaData,
  getQuotaTeamsThunk,
  deleteQuotaTeamThunk,
  getQuotaTeamsDrilldownThunk,
  getQuotaCompanyThunk,
  deleteQuotaCompanyThunk,
  getCurrentAndFutureTeamQuotasThunk,
  getCurrentAndFutureCompanyQuotasThunk,
  createTeamQuotaThunk,
  createTeamQuotaOverrideThunk,
  createCompanyQuotaThunk,
  createCompanyQuotaOverrideThunk,
  getAvailableFiscalQuarters,
  updateQuotaWithOptimisticLock,
  updateTeamQuotaWithOptimisticLock,
  updateCompanyQuotaWithOptimisticLock,
} from "./management.thunk";
import { bucketByQuarterToTeams, bucketByTeamAndQuarter } from "./utils";

// Utility function to ensure Quota and NQP_GOAL are numbers (no rounding)
const transformQuotaData = (data) => {
  if (!Array.isArray(data)) return data;
  return data.map((item) => {
    const transformedItem = { ...item };

    // Convert to number but preserve decimal precision
    if (transformedItem.Quota !== undefined && transformedItem.Quota !== null) {
      transformedItem.Quota = Number(transformedItem.Quota);
    }
    if (transformedItem.NQP_GOAL !== undefined && transformedItem.NQP_GOAL !== null) {
      transformedItem.NQP_GOAL = Number(transformedItem.NQP_GOAL);
    }

    return transformedItem;
  });
};

const initialState = {
  quotaFiscalQuarters: null,
  quotaData: null,
  quotaDataCopy: null,
  teamQuota: null,
  teamQuotaCopy: null,
  companyQuotaCopy: null,
  companyQuota: null,
  updatedRecords: null,
  notUpdatedRecords: null,
  teams: null,
  rampStatus: null,
  updateFailed: null,
  teamQuarters: null,
  teamsRemoval: null,
  fiscalQuarters: null,
  teamHierarchy: null,
  removeQuotaMessage: null,
  removeQuotaSuccess: null,
  deleteQuotaMessage: null,
  quotaAddingReps: null,
  quotaAddingRepsSuccess: null,
  quotaAddingRepInfo: null,
  quotaRemovalModalInfo: null,
  quotaAddingQuarters: null,
  currentQuotaData: null,
  isSuccess: null,
  currentFiscalQuarter: null,
  tempHireDate: null,
  tempTeam: null,
  isCreateSuccess: null,
  currentAndFutureTeamQuotas: null,
  currentAndFutureCompanyQuotas: null,
  availableFiscalQuarters: null,
  availableCurrentFiscalQuarter: null,
  // For optimistic lock updates (rep quota)
  failedUpdates: null, // Array of failed rep quota updates with current DB values
  successfulUpdates: null, // Array of successful rep quota updates
  updateMessage: null, // Rep quota update status message
  // For optimistic lock updates (team quota)
  teamFailedUpdates: null,
  teamSuccessfulUpdates: null,
  teamUpdateMessage: null,
  // For optimistic lock updates (company quota)
  companyFailedUpdates: null,
  companySuccessfulUpdates: null,
  companyUpdateMessage: null,
};
const managementSlice = createSlice({
  name: "skygeni-management",
  initialState,
  reducers: {
    clearManagementSlice: (state, action) => {
      state.quotaData = null;
      state.quotaFiscalQuarters = null;
      state.quotaDataCopy = null;
      state.teamQuota = null;
      state.teamQuotaCopy = null;
      state.companyQuota = null;
      state.companyQuotaCopy = null;
      state.updatedRecords = null;
      state.teams = null;
      state.rampStatus = null;
      state.updateFailed = null;
      state.notUpdatedRecords = null;
      state.teamHierarchy = null;
      state.teamsRemoval = null
      state.teamQuarters = null
      state.fiscalQuarters = null
      state.removeQuotaMessage = null;
      state.removeQuotaSuccess = null;
      state.deleteQuotaMessage = null;
      state.quotaAddingReps = null;
      state.quotaAddingRepsSuccess = null;
      state.quotaAddingRepInfo = null;
      state.quotaRemovalModalInfo = null;
      state.quotaAddingQuarters = null;
      state.currentQuotaData = null
      state.isSuccess = null
      state.currentFiscalQuarter = null;
      state.tempHireDate = null;
      state.tempTeam = null;
      state.isCreateSuccess = null;
      state.availableFiscalQuarters = null;
      state.availableCurrentFiscalQuarter = null;
    },
    clearCurrentQuotaData: (state, action) => {
      state.currentQuotaData = null
    },
    clearQuotaRemovalModalInfo: (state, action) => {
      state.quotaRemovalModalInfo = null
    },
    clearRemoveQuotaReps: (state, action) => {
      state.removeQuotaMessage = null
    },
    clearQuotaAddingRepInfo: (state, action) => {
      state.quotaAddingRepInfo = null
    },
    clearUpdateQuotaData: (state, action) => {
      state.quotaData = null;
      state.quotaDataCopy = null;
      state.teamQuota = null;
      state.teamQuotaCopy = null;
      state.companyQuota = null;
      state.companyQuotaCopy = null;
      state.updatedRecords = null;
      state.notUpdatedRecords = null;
      state.quotaAddingQuarters = null;
    },
    setTempHireDate: (state, action) => {
      state.tempHireDate = action.payload;
    },
    setTempTeam: (state, action) => {
      state.tempTeam = action.payload;
    },
    clearCreateSuccess: (state, action) => {
      state.isCreateSuccess = null;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(fetchCompanyQuotaData.pending, (state, action) => {
      state.companyQuota = null;
      state.updatedRecords = null;
      state.companyQuotaCopy = null;
    });
    builder.addCase(fetchCompanyQuotaData.fulfilled, (state, action) => {
      const transformedData = transformQuotaData(action.payload.message);
      state.companyQuota = transformedData;
      state.companyQuotaCopy = transformedData;
    });
    builder.addCase(fetchTeamQuotaData.pending, (state, action) => {
      state.teamQuota = null;
      state.updatedRecords = null;
      state.teamQuotaCopy = null;
    });
    builder.addCase(fetchTeamQuotaData.fulfilled, (state, action) => {
      const transformedData = transformQuotaData(action.payload.message);
      state.teamQuota = transformedData;
      state.teamQuotaCopy = transformedData;
    });
    builder.addCase(fetchQuotaData.pending, (state, action) => {
      state.quotaData = null;
      state.quotaDataCopy = null;
      state.updatedRecords = null;
    });

    builder.addCase(fetchQuotaData.fulfilled, (state, action) => {
      const data = action.payload.message;
      const transformedData = transformQuotaData(data);
      const teams = [...new Set(transformedData.map((d) => d.Team_Assignment))];
      const rampStatus = ["Ramping", "Ramped"];
      state.quotaData = transformedData;
      state.quotaDataCopy = transformedData;
      state.teams = teams;
      state.rampStatus = rampStatus;
    });
    builder.addCase(updateQuotaData.pending, (state, action) => {
      state.updatedRecords = null;
      state.quotaData = null;
      state.updateFailed = null;
    });
    builder.addCase(updateQuotaData.fulfilled, (state, action) => {
      const updatedRecords = transformQuotaData(action.payload.message.updatedRecords);
      const notUpdatedRecords = transformQuotaData(action.payload.message.notUpdatedRecords);
      // Merge updatedRecords and notUpdatedRecords, deduplicate by UserId and FiscalQuarter
      const mergedMap = new Map();
      (updatedRecords || []).forEach((rec) => {
        const key = `${rec.UserId}_${rec.FiscalQuarter}`;
        mergedMap.set(key, rec);
      });
      (notUpdatedRecords || []).forEach((rec) => {
        const key = `${rec.UserId}_${rec.FiscalQuarter}`;
        if (!mergedMap.has(key)) {
          mergedMap.set(key, rec);
        }
      });
      state.updatedRecords = Array.from(mergedMap.values());
      state.notUpdatedRecords = notUpdatedRecords;
      const updatedQuotaState = state.quotaDataCopy.map((it) => {
        const value = updatedRecords.filter(
          (u) => it.UserId === u.UserId && it.FiscalQuarter === u.FiscalQuarter
        )[0];
        const notUpdatedValue = notUpdatedRecords.filter(
          (u) => it.UserId === u.UserId && it.FiscalQuarter === u.FiscalQuarter
        )[0];
        if (value) {
          const {
            Ramp_Status,
            Team_Assignment,
            UserId,
            Quota,
            NQP_GOAL,
            FullName,
            FiscalQuarter,
            isUpdated,
          } = value;
          return {
            Ramp_Status,
            Team_Assignment,
            UserId,
            Quota,
            NQP_GOAL,
            FullName,
            FiscalQuarter,
            isUpdated,
          };
        } else if (notUpdatedValue) {
          const {
            Ramp_Status,
            Team_Assignment,
            UserId,
            Quota,
            NQP_GOAL,
            FullName,
            FiscalQuarter,
            isUpdated,
          } = notUpdatedValue;
          return {
            Ramp_Status,
            Team_Assignment,
            UserId,
            Quota,
            NQP_GOAL,
            FullName,
            FiscalQuarter,
            isUpdated,
          };
        } else {
          return { ...it };
        }
      });
      state.quotaData = updatedQuotaState;
      state.quotaDataCopy = updatedQuotaState;
      // console.log(updatedQuotaState);
    });
    builder.addCase(updateTeamQuotaData.pending, (state, action) => {
      state.updatedRecords = null;
      state.teamQuota = null;
      state.updateFailed = null;
    });
    builder.addCase(updateTeamQuotaData.fulfilled, (state, action) => {
      const updatedRecords = transformQuotaData(action.payload.message.updatedRecords);
      const notUpdatedRecords = transformQuotaData(action.payload.message.notUpdatedRecords);
      // Merge updatedRecords and notUpdatedRecords, deduplicate by Team and FiscalQuarter
      const mergedMap = new Map();
      (updatedRecords || []).forEach((rec) => {
        const key = `${rec.Team}_${rec.FiscalQuarter}`;
        mergedMap.set(key, rec);
      });
      (notUpdatedRecords || []).forEach((rec) => {
        const key = `${rec.Team}_${rec.FiscalQuarter}`;
        if (!mergedMap.has(key)) {
          mergedMap.set(key, rec);
        }
      });
      state.updatedRecords = Array.from(mergedMap.values());
      state.notUpdatedRecords = notUpdatedRecords;
      const updatedQuotaState = state.teamQuotaCopy.map((it) => {
        const value = updatedRecords.filter(
          (u) => it.Team === u.Team && it.FiscalQuarter === u.FiscalQuarter
        )[0];
        const notUpdatedValue = notUpdatedRecords.filter(
          (u) => it.Team === u.Team && it.FiscalQuarter === u.FiscalQuarter
        )[0];
        if (value) {
          const { Team, FiscalQuarter, Quota, NQP_GOAL, isUpdated } = value;
          return { Team, FiscalQuarter, Quota, NQP_GOAL, isUpdated };
        } else if (notUpdatedValue) {
          const { Team, FiscalQuarter, Quota, NQP_GOAL, isUpdated } = notUpdatedValue;
          return { Team, FiscalQuarter, Quota, NQP_GOAL, isUpdated };
        }
        return { ...it };
      });
      state.teamQuota = updatedQuotaState;
      state.teamQuotaCopy = updatedQuotaState;
    });
    builder.addCase(updateCompanyQuotaData.pending, (state, action) => {
      state.updatedRecords = null;
      state.updateFailed = null;
      state.companyQuota = null;
    });
    builder.addCase(updateCompanyQuotaData.fulfilled, (state, action) => {
      const updatedRecords = transformQuotaData(action.payload.message.updatedRecords);
      const notUpdatedRecords = transformQuotaData(action.payload.message.notUpdatedRecords);
      // Merge updatedRecords and notUpdatedRecords, deduplicate by FiscalQuarter
      const mergedMap = new Map();
      (updatedRecords || []).forEach((rec) => {
        const key = `${rec.FiscalQuarter}`;
        mergedMap.set(key, rec);
      });
      (notUpdatedRecords || []).forEach((rec) => {
        const key = `${rec.FiscalQuarter}`;
        if (!mergedMap.has(key)) {
          mergedMap.set(key, rec);
        }
      });
      state.updatedRecords = Array.from(mergedMap.values());
      state.notUpdatedRecords = notUpdatedRecords;
      const updatedQuotaState = state.companyQuotaCopy.map((it) => {
        const value = updatedRecords.filter(
          (u) => it.FiscalQuarter === u.FiscalQuarter
        )[0];
        const notUpdatedValue = notUpdatedRecords.filter(
          (u) => it.FiscalQuarter === u.FiscalQuarter
        )[0];
        if (value) {
          const { FiscalQuarter, Quota, NQP_GOAL, isUpdated } = value;
          return { FiscalQuarter, Quota, NQP_GOAL, isUpdated };
        } else if (notUpdatedValue) {
          const { FiscalQuarter, Quota, NQP_GOAL, isUpdated } = notUpdatedValue;
          return { FiscalQuarter, Quota, NQP_GOAL, isUpdated };
        }
        return { ...it };
      });
      state.companyQuota = updatedQuotaState;
      state.companyQuotaCopy = updatedQuotaState;
    });

    builder.addCase(
      fetchQuotaFiscalQuartersFilters.pending,
      (state, action) => {
        state.quotaFiscalQuarters = null;
      }
    );
    builder.addCase(
      fetchQuotaFiscalQuartersFilters.fulfilled,
      (state, action) => {
        state.quotaFiscalQuarters = action.payload.message;
      }
    );
    builder.addCase(updateCompanyQuotaData.rejected, (state, action) => {
      state.updateFailed = true;
    });
    builder.addCase(updateTeamQuotaData.rejected, (state, action) => {
      state.updateFailed = true;
    });
    builder.addCase(updateQuotaData.rejected, (state, action) => {
      state.updateFailed = true;
    });
    builder.addCase(getTeamAndFiscalQuarters.pending, (state, action) => {
      state.teamQuarters = null;
      state.teamsRemoval = null;
      state.fiscalQuarters = null;
      state.teamHierarchy = null;
      state.currentFiscalQuarter = null;
      state.removeQuotaSuccess = null;
    });
    builder.addCase(getTeamAndFiscalQuarters.fulfilled, (state, action) => {
      const quarters = action.payload.message.quarters
      
      state.teamQuarters = {}
      state.teamsRemoval = []
      state.fiscalQuarters = quarters
      state.teamHierarchy = action.payload.message.parentChildrenTeamFilter
      state.currentFiscalQuarter = action.payload.message.currentFiscalQuarter
      state.removeQuotaSuccess = null
    });
    builder.addCase(removeQuotaForReps.fulfilled, (state, action) => {
      // Response: { message: [...], success: true }
      state.removeQuotaMessage = action.payload.message;
      state.removeQuotaSuccess = action.payload.success;
    });
    builder.addCase(createQuotaInfo.pending, (state, action) => {
      state.currentQuotaData = null
      state.isSuccess = null
      state.isCreateSuccess = false
    })
    builder.addCase(createQuotaInfo.rejected, (state, action) => {
      console.log('createQuotaInfo.rejected action:', action);
      const message = action.payload?.message ?? action.error?.message ?? null;
      state.currentQuotaData = message;
      state.isSuccess = false;
      state.isCreateSuccess = false;
    })
    builder.addCase(createQuotaInfo.fulfilled, (state, action) => {
      if (action.payload.success === false) {
        state.currentQuotaData = action.payload.message
        state.isSuccess = false
        state.isCreateSuccess = false
        return
      }
      // const userIdInsertSuccessMap = action.payload.message.reduce((acc, val) => {
      //   acc[val.userId] = val.isInsertSuccess;
      //   return acc;
      // }, {});


      // const quotaAddingRepsUpdated = state.quotaAddingReps.filter(it => {
      //   console.log(it, 'quotaAddingRepsUpdated');
      //   // Keep only users who did NOT have a successful insert
      //   return userIdInsertSuccessMap[it.UserID] !== true;
      // });

      // console.log(quotaAddingRepsUpdated, 'quotaAddingRepsUpdated');
      // state.quotaAddingReps = quotaAddingRepsUpdated;
      state.isSuccess = true
      state.currentQuotaData = null
      state.isCreateSuccess = true
    });
    builder.addCase(createQuotaInfoOverride.pending, (state, action) => {
      state.currentQuotaData = null
      state.isSuccess = null
      state.isCreateSuccess = false
    })
    builder.addCase(createQuotaInfoOverride.rejected, (state, action) => {
      console.log('createQuotaInfoOverride.rejected action:', action);
      const message = action.payload?.message ?? action.error?.message ?? null;
      state.currentQuotaData = message;
      state.isSuccess = false;
      state.isCreateSuccess = false;
    })
    builder.addCase(createQuotaInfoOverride.fulfilled, (state, action) => {
      if (action.payload.success === false) {
        state.currentQuotaData = action.payload.message
        state.isSuccess = false
        state.isCreateSuccess = false
        return
      }
      state.isSuccess = true
      state.currentQuotaData = null
      state.isCreateSuccess = true
    });


    builder.addCase(deleteQuotaRemoval.fulfilled, (state, action) => {
      // Response: { message: { userId, fiscalQuarters, team }, success: true }
      state.deleteQuotaMessage = action.payload.message;
      // Remove deleted quarters from quotaRemovalModalInfo
      if (state.quotaRemovalModalInfo && Array.isArray(state.quotaRemovalModalInfo)) {
        const { userId, fiscalQuarters, team } = action.payload.message;
        const val = state.quotaRemovalModalInfo.filter(
          (item) =>
            item.userId !== userId ||
            !fiscalQuarters.includes(item.fiscalQuarter)
        );
        if(val.length === 0){
          state.removeQuotaMessage = state.removeQuotaMessage.filter(it=> it.userId !== userId)
        }
        state.quotaRemovalModalInfo = val
      }
    });

    builder.addCase(getQuotaAddingReps.fulfilled, (state, action) => {
      // Response: { message: [...], success: true }
      state.quotaAddingReps = action.payload.message;
      state.quotaAddingRepsSuccess = action.payload.success;
    });

    builder.addCase(getQuotaAddingRepInfo.fulfilled, (state, action) => {
      // Response: { ...repInfoObject }
      state.quotaAddingRepInfo = action.payload.message;
    });

    builder.addCase(getQuotaAddingQuarters.fulfilled, (state, action) => {
      state.quotaAddingQuarters = action.payload.message;
    });

    builder.addCase(removeQuotaForRepsQuarterInfo.fulfilled, (state, action) => {
      state.quotaRemovalModalInfo = action.payload.message;
    });

    builder.addCase(getQuotaTeamsThunk.fulfilled, (state, action) => {
      state.removeQuotaMessage = action.payload.message;
      state.removeQuotaSuccess = action.payload.success;
    });

    builder.addCase(getQuotaTeamsDrilldownThunk.fulfilled, (state, action) => {
      state.quotaRemovalModalInfo = action.payload.message;
    });

    builder.addCase(deleteQuotaTeamThunk.fulfilled, (state, action) => {
      state.deleteQuotaMessage = action.payload.message;
      // Remove deleted quarters from quotaRemovalModalInfo if it exists and matches
      if (state.quotaRemovalModalInfo && Array.isArray(state.quotaRemovalModalInfo)) {
        const { team, fiscalQuarters } = action.payload.message;
        // Filter out items that match the deleted team and fiscal quarters
        state.quotaRemovalModalInfo = state.quotaRemovalModalInfo.filter(
          (item) => item.team !== team || !fiscalQuarters.includes(item.fiscalQuarter)
        );
      }
    });

    builder.addCase(getQuotaCompanyThunk.fulfilled, (state, action) => {
      state.removeQuotaMessage = action.payload.message;
      state.removeQuotaSuccess = action.payload.success;
    });

    builder.addCase(deleteQuotaCompanyThunk.fulfilled, (state, action) => {
      state.deleteQuotaMessage = action.payload.message;
      if (state.quotaRemovalModalInfo && Array.isArray(state.quotaRemovalModalInfo)) {
        const { fiscalQuarter } = action.payload.message;
        state.quotaRemovalModalInfo = state.quotaRemovalModalInfo.filter(
          (item) => item.fiscalQuarter !== fiscalQuarter
        );
      }
    });

    builder.addCase(getCurrentAndFutureTeamQuotasThunk.fulfilled, (state, action) => {
      state.currentAndFutureTeamQuotas = action.payload.message;
    });

    builder.addCase(getCurrentAndFutureCompanyQuotasThunk.fulfilled, (state, action) => {
      state.currentAndFutureCompanyQuotas = action.payload.message;
    });

    builder.addCase(createTeamQuotaThunk.pending, (state, action) => {
      state.currentQuotaData = null;
      state.isSuccess = null;
      state.isCreateSuccess = false;
    });

    builder.addCase(createTeamQuotaThunk.rejected, (state, action) => {
      state.currentQuotaData = action.payload?.message || null;
      state.isSuccess = false;
      state.isCreateSuccess = false;
    });

    builder.addCase(createTeamQuotaThunk.fulfilled, (state, action) => {
      if (action.payload.success === false) {
        state.currentQuotaData = action.payload.message;
        state.isSuccess = false;
        state.isCreateSuccess = false;
        return;
      }
      state.isSuccess = true;
      state.currentQuotaData = null;
      state.isCreateSuccess = true;
    });

    builder.addCase(createTeamQuotaOverrideThunk.pending, (state, action) => {
      state.currentQuotaData = null;
      state.isSuccess = null;
      state.isCreateSuccess = false;
    });

    builder.addCase(createTeamQuotaOverrideThunk.rejected, (state, action) => {
      state.currentQuotaData = action.payload?.message || null;
      state.isSuccess = false;
      state.isCreateSuccess = false;
    });

    builder.addCase(createTeamQuotaOverrideThunk.fulfilled, (state, action) => {
      if (action.payload.success === false) {
        state.currentQuotaData = action.payload.message;
        state.isSuccess = false;
        state.isCreateSuccess = false;
        return;
      }
      state.isSuccess = true;
      state.currentQuotaData = null;
      state.isCreateSuccess = true;
    });

    builder.addCase(createCompanyQuotaThunk.pending, (state, action) => {
      state.currentQuotaData = null;
      state.isSuccess = null;
      state.isCreateSuccess = false;
    });

    builder.addCase(createCompanyQuotaThunk.rejected, (state, action) => {
      state.currentQuotaData = action.payload?.message || null;
      state.isSuccess = false;
      state.isCreateSuccess = false;
    });

    builder.addCase(createCompanyQuotaThunk.fulfilled, (state, action) => {
      if (action.payload.success === false) {
        state.currentQuotaData = action.payload.message;
        state.isSuccess = false;
        state.isCreateSuccess = false;
        return;
      }
      state.isSuccess = true;
      state.currentQuotaData = null;
      state.isCreateSuccess = true;
    });

    builder.addCase(createCompanyQuotaOverrideThunk.pending, (state, action) => {
      state.currentQuotaData = null;
      state.isSuccess = null;
      state.isCreateSuccess = false;
    });

    builder.addCase(createCompanyQuotaOverrideThunk.rejected, (state, action) => {
      state.currentQuotaData = action.payload?.message || null;
      state.isSuccess = false;
      state.isCreateSuccess = false;
    });

    builder.addCase(createCompanyQuotaOverrideThunk.fulfilled, (state, action) => {
      if (action.payload.success === false) {
        state.currentQuotaData = action.payload.message;
        state.isSuccess = false;
        state.isCreateSuccess = false;
        return;
      }
      state.isSuccess = true;
      state.currentQuotaData = null;
      state.isCreateSuccess = true;
    });

    // Bulk Upsert Quota Data (for update view)
    builder.addCase(bulkUpsertQuotaData.pending, (state, action) => {
      state.updatedRecords = null;
      state.quotaData = null;
      state.updateFailed = null;
    });
    builder.addCase(bulkUpsertQuotaData.fulfilled, (state, action) => {
      if (action.payload.success === false) {
        state.currentQuotaData = action.payload.message;
        state.isSuccess = false;
        state.updateFailed = true;
        return;
      }

      // Use message property from response and transform the data
      const responseRecords = action.payload.message || [];

      // Transform the response records to match expected format (no rounding - preserve precision)
      const updatedRecords = responseRecords.map(record => ({
        ...record,
        quota: Number(record.quota),
        NQP_Goal: Number(record.NQP_Goal),
        // Map response fields to match database field names
        UserID: record.userId,
        Fiscal_Quarter: record.fiscalQuarter,
        Ramp_Status: record.rampStatus,
        Team: record.team || record.teamAssignment,
        Quota: Number(record.quota),
        NQP_GOAL: Number(record.NQP_Goal),
        Full_Name: record.fullName,
        SFDC_Owner_ID: record.sfdcOwnerId,
      }));

      state.updatedRecords = updatedRecords;

      // Update the quotaData with the new values
      const updatedQuotaState = state.quotaDataCopy.map((it) => {
        const updated = updatedRecords.find(
          (u) => it.UserId === u.UserID && it.FiscalQuarter === u.Fiscal_Quarter
        );
        if (updated) {
          return {
            ...it,
            Ramp_Status: updated.Ramp_Status,
            Team_Assignment: updated.Team,
            Quota: updated.Quota,
            NQP_GOAL: updated.NQP_GOAL,
            isUpdated: true,
          };
        }
        return it;
      });

      state.quotaData = updatedQuotaState;
      state.quotaDataCopy = updatedQuotaState;
      state.isSuccess = true;
    });
    builder.addCase(bulkUpsertQuotaData.rejected, (state, action) => {
      state.updateFailed = true;
    });

    // Bulk Upsert Team Quota Data
    builder.addCase(bulkUpsertTeamQuotaData.pending, (state, action) => {
      state.updatedRecords = null;
      state.teamQuota = null;
      state.updateFailed = null;
    });
    builder.addCase(bulkUpsertTeamQuotaData.fulfilled, (state, action) => {
      if (action.payload.success === false) {
        state.currentQuotaData = action.payload.message;
        state.isSuccess = false;
        state.updateFailed = true;
        return;
      }

      const responseRecords = action.payload.message || [];
      const updatedRecords = responseRecords.map(record => ({
        ...record,
        quota: Number(record.quota),
        NQP_Goal: Number(record.NQP_Goal),
        Team: record.team,
        Quarter: record.fiscalQuarter,
        Quota: Number(record.quota),
        NQP_GOAL: Number(record.NQP_Goal),
      }));

      state.updatedRecords = updatedRecords;

      const updatedTeamQuotaState = state.teamQuotaCopy.map((it) => {
        const updated = updatedRecords.find(
          (u) => it.Team === u.Team && it.FiscalQuarter === u.Quarter
        );
        if (updated) {
          return {
            ...it,
            Quota: updated.Quota,
            NQP_GOAL: updated.NQP_GOAL,
            isUpdated: true,
          };
        }
        return it;
      });

      state.teamQuota = updatedTeamQuotaState;
      state.teamQuotaCopy = updatedTeamQuotaState;
      state.isSuccess = true;
    });
    builder.addCase(bulkUpsertTeamQuotaData.rejected, (state, action) => {
      state.updateFailed = true;
    });

    // Bulk Upsert Company Quota Data
    builder.addCase(bulkUpsertCompanyQuotaData.pending, (state, action) => {
      state.updatedRecords = null;
      state.companyQuota = null;
      state.updateFailed = null;
    });
    builder.addCase(bulkUpsertCompanyQuotaData.fulfilled, (state, action) => {
      if (action.payload.success === false) {
        state.currentQuotaData = action.payload.message;
        state.isSuccess = false;
        state.updateFailed = true;
        return;
      }

      const responseRecords = action.payload.message || [];
      const updatedRecords = responseRecords.map(record => ({
        ...record,
        quota: Number(record.quota),
        NQP_Goal: Number(record.NQP_Goal),
        Fiscal_Quarter: record.fiscalQuarter,
        Quota: Number(record.quota),
        NQP_GOAL: Number(record.NQP_Goal),
      }));

      state.updatedRecords = updatedRecords;

      const updatedCompanyQuotaState = state.companyQuotaCopy.map((it) => {
        const updated = updatedRecords.find(
          (u) => it.FiscalQuarter === u.Fiscal_Quarter
        );
        if (updated) {
          return {
            ...it,
            Quota: updated.Quota,
            NQP_GOAL: updated.NQP_GOAL,
            isUpdated: true,
          };
        }
        return it;
      });

      state.companyQuota = updatedCompanyQuotaState;
      state.companyQuotaCopy = updatedCompanyQuotaState;
      state.isSuccess = true;
    });
    builder.addCase(bulkUpsertCompanyQuotaData.rejected, (state, action) => {
      state.updateFailed = true;
    });

    // Get Available Fiscal Quarters for Update Section
    builder.addCase(getAvailableFiscalQuarters.pending, (state, action) => {
      state.availableFiscalQuarters = null;
      state.availableCurrentFiscalQuarter = null;
    });
    builder.addCase(getAvailableFiscalQuarters.fulfilled, (state, action) => {
      state.availableFiscalQuarters = action.payload.message.availableFiscalQuarters;
      state.availableCurrentFiscalQuarter = action.payload.message.currentFiscalQuarter;
    });
    builder.addCase(getAvailableFiscalQuarters.rejected, (state, action) => {
      state.availableFiscalQuarters = null;
      state.availableCurrentFiscalQuarter = null;
    });

    // Update Quota with Optimistic Locking
    builder.addCase(updateQuotaWithOptimisticLock.pending, (state, action) => {
      state.failedUpdates = null;
      state.successfulUpdates = null;
      state.updateMessage = null;
      state.updateFailed = false;
    });
    builder.addCase(updateQuotaWithOptimisticLock.fulfilled, (state, action) => {
      const { successfulUpdates, failedUpdates, message } = action.payload.message;

      state.successfulUpdates = successfulUpdates;
      state.failedUpdates = failedUpdates;
      state.updateMessage = message;

      // Update quotaData with successful updates
      if (successfulUpdates && successfulUpdates.length > 0) {
        const updatedQuotaData = state.quotaData.map((item) => {
          const successUpdate = successfulUpdates.find(
            (u) => u.UserId === item.UserId && u.FiscalQuarter === item.FiscalQuarter
          );

          if (successUpdate) {
            return {
              ...item,
              Quota: successUpdate.currentQuota !== undefined ? successUpdate.currentQuota : item.Quota,
              NQP_GOAL: successUpdate.currentNQP_GOAL !== undefined ? successUpdate.currentNQP_GOAL : item.NQP_GOAL,
              Team_Assignment: successUpdate.currentTeam_Assignment !== undefined ? successUpdate.currentTeam_Assignment : item.Team_Assignment,
              Ramp_Status: successUpdate.currentRamp_Status !== undefined ? successUpdate.currentRamp_Status : item.Ramp_Status,
              isUpdated: true,
              updateSuccess: true,
            };
          }

          return item;
        });

        state.quotaData = updatedQuotaData;
        state.quotaDataCopy = updatedQuotaData;
      }

      // Mark failed updates in the state with current DB values
      if (failedUpdates && failedUpdates.length > 0) {
        const updatedQuotaDataWithFailures = state.quotaData.map((item) => {
          const failedUpdate = failedUpdates.find(
            (u) => u.UserId === item.UserId && u.FiscalQuarter === item.FiscalQuarter
          );

          if (failedUpdate) {
            return {
              ...item,
              // Update with current DB values
              Quota: failedUpdate.currentQuota !== undefined ? failedUpdate.currentQuota : item.Quota,
              NQP_GOAL: failedUpdate.currentNQP_GOAL !== undefined ? failedUpdate.currentNQP_GOAL : item.NQP_GOAL,
              Team_Assignment: failedUpdate.currentTeam_Assignment !== undefined ? failedUpdate.currentTeam_Assignment : item.Team_Assignment,
              Ramp_Status: failedUpdate.currentRamp_Status !== undefined ? failedUpdate.currentRamp_Status : item.Ramp_Status,
              updateFailed: true,
              updateSuccess: false,
              failureReason: failedUpdate.reason,
            };
          }

          return item;
        });

        state.quotaData = updatedQuotaDataWithFailures;
        state.quotaDataCopy = updatedQuotaDataWithFailures;
      }

      state.isSuccess = failedUpdates && failedUpdates.length === 0;
    });
    builder.addCase(updateQuotaWithOptimisticLock.rejected, (state, action) => {
      state.updateFailed = true;
      state.failedUpdates = null;
      state.successfulUpdates = null;
      state.updateMessage = 'Update failed. Please try again.';
    });

    // Update Team Quota with Optimistic Locking
    builder.addCase(updateTeamQuotaWithOptimisticLock.pending, (state, action) => {
      state.teamFailedUpdates = null;
      state.teamSuccessfulUpdates = null;
      state.teamUpdateMessage = null;
      state.updateFailed = false;
    });
    builder.addCase(updateTeamQuotaWithOptimisticLock.fulfilled, (state, action) => {
      const { successfulUpdates, failedUpdates, message } = action.payload.message;

      state.teamSuccessfulUpdates = successfulUpdates;
      state.teamFailedUpdates = failedUpdates;
      state.teamUpdateMessage = message;

      // Update teamQuota with successful updates
      if (successfulUpdates && successfulUpdates.length > 0) {
        const updatedTeamQuota = state.teamQuota.map((item) => {
          const successUpdate = successfulUpdates.find(
            (u) => u.Team === item.Team && u.FiscalQuarter === item.FiscalQuarter
          );

          if (successUpdate) {
            return {
              ...item,
              Quota: successUpdate.currentQuota !== undefined ? successUpdate.currentQuota : item.Quota,
              NQP_GOAL: successUpdate.currentNQP_GOAL !== undefined ? successUpdate.currentNQP_GOAL : item.NQP_GOAL,
              isUpdated: true,
              updateSuccess: true,
            };
          }

          return item;
        });

        state.teamQuota = updatedTeamQuota;
        state.teamQuotaCopy = updatedTeamQuota;
      }

      // Mark failed updates in the state with current DB values
      if (failedUpdates && failedUpdates.length > 0) {
        const updatedTeamQuotaWithFailures = state.teamQuota.map((item) => {
          const failedUpdate = failedUpdates.find(
            (u) => u.Team === item.Team && u.FiscalQuarter === item.FiscalQuarter
          );

          if (failedUpdate) {
            return {
              ...item,
              Quota: failedUpdate.currentQuota !== undefined ? failedUpdate.currentQuota : item.Quota,
              NQP_GOAL: failedUpdate.currentNQP_GOAL !== undefined ? failedUpdate.currentNQP_GOAL : item.NQP_GOAL,
              updateFailed: true,
              updateSuccess: false,
              failureReason: failedUpdate.reason,
            };
          }

          return item;
        });

        state.teamQuota = updatedTeamQuotaWithFailures;
        state.teamQuotaCopy = updatedTeamQuotaWithFailures;
      }

      state.isSuccess = failedUpdates && failedUpdates.length === 0;
    });
    builder.addCase(updateTeamQuotaWithOptimisticLock.rejected, (state, action) => {
      state.updateFailed = true;
      state.teamFailedUpdates = null;
      state.teamSuccessfulUpdates = null;
      state.teamUpdateMessage = 'Team quota update failed. Please try again.';
    });

    // Update Company Quota with Optimistic Locking
    builder.addCase(updateCompanyQuotaWithOptimisticLock.pending, (state, action) => {
      state.companyFailedUpdates = null;
      state.companySuccessfulUpdates = null;
      state.companyUpdateMessage = null;
      state.updateFailed = false;
    });
    builder.addCase(updateCompanyQuotaWithOptimisticLock.fulfilled, (state, action) => {
      const { successfulUpdates, failedUpdates, message } = action.payload.message;

      state.companySuccessfulUpdates = successfulUpdates;
      state.companyFailedUpdates = failedUpdates;
      state.companyUpdateMessage = message;

      // Update companyQuota with successful updates
      if (successfulUpdates && successfulUpdates.length > 0) {
        const updatedCompanyQuota = state.companyQuota.map((item) => {
          const successUpdate = successfulUpdates.find(
            (u) => u.FiscalQuarter === item.FiscalQuarter
          );

          if (successUpdate) {
            return {
              ...item,
              Quota: successUpdate.currentQuota !== undefined ? successUpdate.currentQuota : item.Quota,
              NQP_GOAL: successUpdate.currentNQP_GOAL !== undefined ? successUpdate.currentNQP_GOAL : item.NQP_GOAL,
              isUpdated: true,
              updateSuccess: true,
            };
          }

          return item;
        });

        state.companyQuota = updatedCompanyQuota;
        state.companyQuotaCopy = updatedCompanyQuota;
      }

      // Mark failed updates in the state with current DB values
      if (failedUpdates && failedUpdates.length > 0) {
        const updatedCompanyQuotaWithFailures = state.companyQuota.map((item) => {
          const failedUpdate = failedUpdates.find(
            (u) => u.FiscalQuarter === item.FiscalQuarter
          );

          if (failedUpdate) {
            return {
              ...item,
              Quota: failedUpdate.currentQuota !== undefined ? failedUpdate.currentQuota : item.Quota,
              NQP_GOAL: failedUpdate.currentNQP_GOAL !== undefined ? failedUpdate.currentNQP_GOAL : item.NQP_GOAL,
              updateFailed: true,
              updateSuccess: false,
              failureReason: failedUpdate.reason,
            };
          }

          return item;
        });

        state.companyQuota = updatedCompanyQuotaWithFailures;
        state.companyQuotaCopy = updatedCompanyQuotaWithFailures;
      }

      state.isSuccess = failedUpdates && failedUpdates.length === 0;
    });
    builder.addCase(updateCompanyQuotaWithOptimisticLock.rejected, (state, action) => {
      state.updateFailed = true;
      state.companyFailedUpdates = null;
      state.companySuccessfulUpdates = null;
      state.companyUpdateMessage = 'Company quota update failed. Please try again.';
    });
  },
});

export const selectQuotaFiscalQuarters = (state) =>
  state.management.quotaFiscalQuarters;
export const selectTeamQuotaData = (state) => state.management.teamQuota;
export const selectCompanyQuotaData = (state) => state.management.companyQuota;
export const selectQuotaData = (state) => state.management.quotaData;
export const selectTeams = (state) => state.management.teams;
export const selectRampStatus = (state) => state.management.rampStatus;
export const selectUpdatedRecords = (state) => state.management.updatedRecords;
export const { clearManagementSlice, clearCurrentQuotaData, clearQuotaRemovalModalInfo, clearRemoveQuotaReps, clearQuotaAddingRepInfo, clearUpdateQuotaData, setTempHireDate, setTempTeam, clearCreateSuccess } = managementSlice.actions;
export const selectTeamQuarters = (state) => state.management.teamQuarters;
export const selecTeamsRemoval = (state) => state.management.teamsRemoval;
export const selectFiscalQuarters = (state) => state.management.fiscalQuarters;
export const selectTeamHierarchy = (state) => state.management.teamHierarchy;
export const selectUpdateFailed = (state) => state.management.updateFailed;
export const selectRemoveQuotaMessage = (state) => state.management.removeQuotaMessage;
export const selectRemoveQuotaSuccess = (state) => state.management.removeQuotaSuccess;
export const selectDeleteQuotaMessage = (state) => state.management.deleteQuotaMessage;
export const selectQuotaAddingReps = (state) => state.management.quotaAddingReps;
export const selectQuotaAddingRepsSuccess = (state) => state.management.quotaAddingRepsSuccess;
export const selectQuotaAddingRepInfo = (state) => state.management.quotaAddingRepInfo;
export const selectQuotaRemovalModalInfo = (state) => state.management.quotaRemovalModalInfo;
export const selectQuotaAddingQuarters = (state) => state.management.quotaAddingQuarters;
export const selectCurrentQuotaData = (state) => state.management.currentQuotaData;
export const selectCurrentFiscalQuarter = (state) => state.management.currentFiscalQuarter;
export const selectIsSuccess = (state) => state.management.isSuccess;
export const selectTempHireDate = (state) => state.management.tempHireDate;
export const selectTempTeam = (state) => state.management.tempTeam;
export const selectIsCreateSuccess = (state) => state.management.isCreateSuccess
export const selectCurrentAndFutureTeamQuotas = (state) => state.management.currentAndFutureTeamQuotas;
export const selectCurrentAndFutureCompanyQuotas = (state) => state.management.currentAndFutureCompanyQuotas;
export const selectFailedUpdates = (state) => state.management.failedUpdates;
export const selectSuccessfulUpdates = (state) => state.management.successfulUpdates;
export const selectUpdateMessage = (state) => state.management.updateMessage;
export const selectTeamFailedUpdates = (state) => state.management.teamFailedUpdates;
export const selectTeamSuccessfulUpdates = (state) => state.management.teamSuccessfulUpdates;
export const selectTeamUpdateMessage = (state) => state.management.teamUpdateMessage;
export const selectCompanyFailedUpdates = (state) => state.management.companyFailedUpdates;
export const selectCompanySuccessfulUpdates = (state) => state.management.companySuccessfulUpdates;
export const selectCompanyUpdateMessage = (state) => state.management.companyUpdateMessage;
export const selectAvailableFiscalQuarters = (state) => state.management.availableFiscalQuarters;
export const selectAvailableCurrentFiscalQuarter = (state) => state.management.availableCurrentFiscalQuarter;
export default managementSlice.reducer;
